import { useTranslation } from "react-i18next";

import { PIPELINE_STAGES } from "../api/types";
import type { JobStatusResponse } from "../api/types";

export interface JobProgressProps {
  status: JobStatusResponse | null;
  isPolling: boolean;
}

type StageState = "pending" | "active" | "done" | "failed";

function stageStateFor(
  stageKey: string,
  current: string | null,
  status: JobStatusResponse | null,
): StageState {
  if (!status) return "pending";
  if (status.status === "failed" && status.current_step === stageKey) {
    return "failed";
  }
  if (status.status === "completed") return "done";
  if (current === stageKey) return "active";

  // If we've passed this stage (current_step appears later in the stage list)
  // mark it as done.
  const stages = PIPELINE_STAGES.map((s) => s.key);
  const currentIdx = current ? stages.indexOf(current as (typeof stages)[number]) : -1;
  const myIdx = stages.indexOf(stageKey as (typeof stages)[number]);
  if (currentIdx >= 0 && myIdx >= 0 && myIdx < currentIdx) return "done";
  return "pending";
}

const STATE_STYLES: Record<StageState, string> = {
  pending: "border-stone-700 bg-stone-900 text-stone-400",
  active: "border-yellow-400 bg-yellow-400/15 text-yellow-50",
  done: "border-green-500 bg-green-500/15 text-green-100",
  failed: "border-red-500 bg-red-500/10 text-red-100",
};

function StageIcon({ state }: { state: StageState }) {
  const commonIconProps = {
    "aria-hidden": true,
    className: "h-3.5 w-3.5",
    fill: "none",
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    strokeWidth: 2,
    viewBox: "0 0 24 24",
  };

  const icon =
    state === "done" ? (
      <svg {...commonIconProps} data-testid="stage-icon-done">
        <path d="m5 12 4 4 10-10" />
      </svg>
    ) : state === "active" ? (
      <svg {...commonIconProps} data-testid="stage-icon-active">
        <path d="M6 2h12" />
        <path d="M6 22h12" />
        <path d="M17 2v5c0 1.5-.8 2.8-2 3.5L12 12l-3-1.5A4 4 0 0 1 7 7V2" />
        <path d="M7 22v-5c0-1.5.8-2.8 2-3.5L12 12l3 1.5a4 4 0 0 1 2 3.5v5" />
      </svg>
    ) : state === "failed" ? (
      <svg {...commonIconProps} data-testid="stage-icon-failed">
        <path d="M6 6l12 12" />
        <path d="M18 6 6 18" />
      </svg>
    ) : (
      <svg {...commonIconProps} data-testid="stage-icon-pending">
        <circle cx="12" cy="12" r="8" />
        <path d="M12 8v5l3 2" />
      </svg>
    );

  return (
    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-stone-100/80 bg-stone-950/60 text-stone-50">
      {icon}
    </span>
  );
}

export function JobProgress({ status, isPolling }: JobProgressProps) {
  const { t } = useTranslation();
  const current = status?.current_step ?? null;
  const overall =
    status?.status === "completed"
      ? t("progress.statuses.completed")
      : status?.status === "failed"
        ? t("progress.statuses.failed")
        : status?.status === "cancelled"
          ? t("progress.statuses.cancelled")
          : isPolling
            ? `${status?.progress ?? 0}%`
            : t("progress.statuses.idle");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-stone-100">
          {t("progress.heading")}
        </h2>
        <span className="text-sm text-stone-300">{overall}</span>
      </div>
      <ol className="space-y-2">
        {PIPELINE_STAGES.map((stage) => {
          const state = stageStateFor(stage.key, current, status);
          const activeClass = state === "active" ? " stage-active-flash" : "";
          return (
            <li
              key={stage.key}
              data-state={state}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${STATE_STYLES[state]}${activeClass}`}
            >
              <span className="flex items-center gap-3">
                <StageIcon state={state} />
                {t(stage.labelKey)}
              </span>
              {state === "active" && (
                <span className="text-xs text-yellow-100">
                  {t("progress.working")}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
